const loadedScripts = new Map<string, Promise<unknown>>();

/**
 * Dynamically loads a script and returns a promise that resolves when it's ready.
 * Caches promises to prevent reloading the same script.
 */
type scriptKey = keyof Window;
function loadScript<T>(src: string, globalName: scriptKey): Promise<T> {
    if (loadedScripts.has(src)) {
        return loadedScripts.get(src)! as Promise<T>;
    }
    const promise = new Promise<T>((resolve, reject) => {
        const result = (window as Window)[globalName] as T | undefined;
        if (result) {
            return resolve(result);
        }
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => resolve((window as Window)[globalName] as T);
        script.onerror = () =>
            reject(new Error(`Failed to load script: ${src}`));
        document.head.appendChild(script);
    });
    loadedScripts.set(src, promise);
    return promise;
}

/**
 * Special handler for the YouTube Iframe API, which uses a global callback.
 */
// Declare YT type globally for TypeScript
declare global {
    interface Window {
        YT: YT;
        onYouTubeIframeAPIReady: () => void;
        Vimeo: Vimeo;
    }
}

// Minimal YT type definition for TypeScript
type YTPlayerState = {
    ENDED: number;
};

type YTPlayerEvents = {
    onStateChange?: (event: { data: number }) => void;
};

type YTPlayerOptions = {
    videoId: string;
    width: string;
    height: string;
    playerVars: {
        autoplay?: number;
        rel?: number;
        modestbranding?: number;
    };
    events: YTPlayerEvents;
};

interface YTPlayer {
    new (element: string | HTMLElement, options: YTPlayerOptions): object;
}

interface YT {
    Player: YTPlayer;
    PlayerState: YTPlayerState;
}


interface Vimeo {
    Player: new(element: HTMLElement) => VimeoPlayer
}
type Callback = () => void;
type VimeoPlayer = HTMLElement & {
    on(evt: string, callback: Callback) : void
}

function loadYouTubeApi(): Promise<YT> {
    const src = "https://www.youtube.com/iframe_api";
    if (loadedScripts.has(src)) {
        return loadedScripts.get(src)! as Promise<YT>;
    }
    const promise = new Promise<YT>((resolve) => {
        if (window.YT && window.YT.Player) {
            return resolve(window.YT);
        }
        const existingCallback = (window as Window).onYouTubeIframeAPIReady;
        (window as Window).onYouTubeIframeAPIReady = () => {
            if (existingCallback) existingCallback();
            resolve(window.YT);
        };
        if (!document.querySelector(`script[src="${src}"]`)) {
            const script = document.createElement("script");
            script.src = src;
            document.head.appendChild(script);
        }
    });
    loadedScripts.set(src, promise);
    return promise;
}

class VideoComponent {
    private playerElementMap = new WeakMap<HTMLElement, HTMLElement | null>();

    constructor() {
        // The component is now initialized by the main script after the DOM is loaded.
        this.initialize();
    }

    private initialize(): void {
        const clickableElements = document.querySelectorAll<HTMLElement>(
            ".video-poster-container"
        );
        clickableElements.forEach((clickableElement) => {
            // The playerHost is the element whose content will be modified (hiding children, adding player).
            // In edit mode, this is the grandparent of the play button. In normal mode, it's the clickable element itself.
            const playerHost = clickableElement.classList.contains(
                "button-play"
            )
                ? clickableElement.parentElement!.parentElement!
                : clickableElement;

            // We use the playerHost as the key to ensure we always restore the correct container.
            this.playerElementMap.set(playerHost, null); // Prime the map

            // Add the click listener to each video poster
            clickableElement.addEventListener(
                "click",
                this.handlePosterClick.bind(this),
                { once: true }
            );
        });
    }

    private async handlePosterClick(event: MouseEvent): Promise<void> {
        event.stopPropagation(); // Prevents the click from bubbling up, which is important in edit mode.
        const clickableElement = event.currentTarget as HTMLElement;

        // Determine the actual container that holds the poster and will host the player.
        const playerHost = clickableElement.classList.contains("button-play")
            ? clickableElement.parentElement!.parentElement!
            : clickableElement;

        const { videoId, videoType } = clickableElement.dataset;

        if (!videoId || !videoType) {
            console.error(
                "Video: Missing video data attributes on clickable element.",
                clickableElement
            );
            this.restorePoster(playerHost);
            return;
        }

        // Hide the original content (poster and play icon) by making it invisible
        Array.from(playerHost.children).forEach((child) => {
            (child as HTMLElement).classList.add(
                "opacity-0",
                "pointer-events-none"
            );
        });

        // Create a container for the player that will be positioned absolutely
        const playerWrapper = document.createElement("div");
        playerWrapper.className = "absolute inset-0 w-full h-full";
        this.playerElementMap.set(playerHost, playerWrapper);

        try {
            if (videoType === "youtube") {
                const YT = await loadYouTubeApi();
                const playerDiv = document.createElement("div");
                playerWrapper.appendChild(playerDiv);
                playerHost.appendChild(playerWrapper);

                new YT.Player(playerDiv, {
                    videoId,
                    width: "100%",
                    height: "100%",
                    playerVars: { autoplay: 1, rel: 0, modestbranding: 1 },
                    events: {
                        onStateChange: (event: { data: number }) => {
                            if (event.data === YT.PlayerState.ENDED) {
                                this.restorePoster(playerHost);
                            }
                        },
                    },
                });
            } else {
                // Vimeo
                const Vimeo = await loadScript<Vimeo>(
                    "https://player.vimeo.com/api/player.js",
                    "Vimeo"
                );
                const iframeSrc = `https://player.vimeo.com/video/${videoId}?autoplay=1&dnt=1`;
                playerWrapper.innerHTML = `<iframe title="Vimeo video player" class="w-full h-full" src="${iframeSrc}" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;
                playerHost.appendChild(playerWrapper);

                const iframe = playerWrapper.querySelector("iframe");
                if (iframe) {
                    const player: VimeoPlayer = new Vimeo.Player(iframe);
                    playerHost.classList.add("video-playing");
                    player.on("ended", () => this.restorePoster(playerHost));
                } else {
                    throw new Error("Vimeo iframe could not be created.");
                }
            }
        } catch (error) {
            console.error("Failed to initialize video player:", error);
            this.restorePoster(playerHost);
        }
    }

    private restorePoster(playerHost: HTMLElement): void {
        // Remove the player
        const playerWrapper = this.playerElementMap.get(playerHost);
        if (playerWrapper) {
            playerWrapper.remove();
            this.playerElementMap.set(playerHost, null); // Reset map entry
        }
        playerHost.classList.remove("video-playing");
        // Show the original content again
        Array.from(playerHost.children).forEach((child) => {
            (child as HTMLElement).classList.remove(
                "opacity-0",
                "pointer-events-none"
            );
        });

        // Find the new clickable element within the restored content.
        // This could be the host itself or a child element (like the play button in edit mode).
        const newClickableElement = playerHost.classList.contains(
            "video-poster-container"
        )
            ? playerHost
            : playerHost.querySelector<HTMLElement>(".video-poster-container");

        if (newClickableElement) {
            // Re-attach the listener so the video can be played again.
            newClickableElement.addEventListener(
                "click",
                this.handlePosterClick.bind(this),
                { once: true }
            );
        }
    }
}

export const initVideo = (): void => {
    // Create an instance of the component to attach its event listeners.
    // Well it is not really a component, it some scripts parked in a class
    // initialization have side effects so if there are elements they will be initialized
    new VideoComponent();
};
